import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { briefingApi } from '../lib/api';
import {
  X, Loader2, Download, FileText, Users, FolderOpen, Brain,
  MessageSquare, AlertTriangle, ArrowRight, Shield, Zap
} from 'lucide-react';

const FitBadge = ({ rating }) => {
  if (!rating || rating === 'N/A') return <span className="text-xs text-[#A3B5C7]">N/A</span>;
  const colors = {
    STRONG: 'bg-green-500/20 text-green-400 border-green-500/30',
    POSSIBLE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    UNLIKELY: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded border ${colors[rating] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {rating}
    </span>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-4 h-4 text-[#00D4C8]" strokeWidth={1.5} />}
      <h4 className="text-xs uppercase tracking-[0.15em] text-[#00D4C8] font-medium">{title}</h4>
    </div>
    <div className="text-sm text-[#D4E2F0] leading-relaxed">{children}</div>
  </div>
);

const BriefingModal = ({ targetId, targetName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await briefingApi.generate(targetId);
      setBriefing(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate briefing');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!briefing?.briefing_id) return;
    setDownloading(true);
    try {
      const res = await briefingApi.downloadPdf(briefing.briefing_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `BREACH_Brief_${targetName?.replace(/\s+/g, '_') || 'target'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-[90vw] max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="briefing-modal"
        >
          {/* Header */}
          <div className="p-6 border-b border-[#1A2F4C] flex items-center justify-between shrink-0">
            <div>
              <h2 className="font-heading text-xl text-white" data-testid="briefing-modal-title">Meeting Brief</h2>
              <p className="text-sm text-[#A3B5C7]">{targetName}</p>
            </div>
            <div className="flex items-center gap-2">
              {briefing && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-2"
                  data-testid="briefing-download-pdf"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  PDF
                </button>
              )}
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1A2F4C] text-[#A3B5C7]">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {!briefing && !loading && (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-[#00D4C8]/40 mx-auto mb-4" />
                <h3 className="text-lg text-white mb-2">Generate Meeting Brief</h3>
                <p className="text-sm text-[#A3B5C7] mb-6 max-w-md mx-auto">
                  Pull together target intelligence, pipeline history, outreach records, and AI-generated talking points into a single pre-meeting briefing.
                </p>
                <button
                  onClick={handleGenerate}
                  className="btn-primary px-6 py-3 text-sm"
                  data-testid="briefing-generate-btn"
                >
                  Generate Briefing
                </button>
              </div>
            )}

            {loading && (
              <div className="text-center py-16">
                <Loader2 className="w-10 h-10 text-[#00D4C8] animate-spin mx-auto mb-4" />
                <p className="text-sm text-[#A3B5C7]">Generating meeting brief...</p>
                <p className="text-xs text-[#A3B5C7]/60 mt-1">Compiling intelligence and AI analysis</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm mb-4">
                {error}
                <button onClick={handleGenerate} className="ml-3 underline hover:no-underline">Retry</button>
              </div>
            )}

            {briefing && (
              <div className="space-y-1">
                {/* AI availability note */}
                {!briefing.ai_available && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs mb-4">
                    AI-generated sections unavailable. Showing compiled intelligence only.
                  </div>
                )}

                {/* Target Overview */}
                <Section title="Target Overview" icon={Users}>
                  <p><strong>{briefing.target_overview?.name}</strong> ({briefing.target_overview?.type})</p>
                  <p className="text-xs text-[#A3B5C7]">{briefing.target_overview?.location}</p>
                  {briefing.target_overview?.key_contacts?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {briefing.target_overview.key_contacts.map((c, i) => (
                        <p key={i} className="text-xs">{c.name} — {c.role}</p>
                      ))}
                    </div>
                  )}
                </Section>

                {/* Projects */}
                {briefing.projects_discussed?.length > 0 && (
                  <Section title="Projects Being Discussed" icon={FolderOpen}>
                    <div className="flex flex-wrap gap-2">
                      {briefing.projects_discussed.map((p, i) => (
                        <span key={i} className="px-2 py-1 text-xs bg-[#00D4C8]/10 text-[#00D4C8] rounded border border-[#00D4C8]/20">{p}</span>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Intelligence Summary */}
                <Section title="Intelligence Summary" icon={Brain}>
                  {briefing.intelligence_summary?.mandate && (
                    <p className="mb-2"><strong className="text-[#A3B5C7] text-xs">Mandate:</strong> {briefing.intelligence_summary.mandate}</p>
                  )}
                  {briefing.intelligence_summary?.greenwash_status && (
                    <p className="text-xs"><strong className="text-[#A3B5C7]">Greenwash:</strong> {briefing.intelligence_summary.greenwash_status}</p>
                  )}
                </Section>

                {/* Offering Fit */}
                {(() => {
                  const legacy = briefing.intelligence_suite_fit;
                  const offerings = (briefing.offering_fit && briefing.offering_fit.length > 0)
                    ? briefing.offering_fit
                    : (legacy && typeof legacy === 'object'
                        ? Object.entries(legacy).map(([k, v]) => ({ offering: k, rating: v?.rating, rationale: v?.rationale }))
                        : []);
                  if (offerings.length === 0) return null;
                  return (
                    <Section title="Offering Fit" icon={Zap}>
                      <div className="grid grid-cols-3 gap-3" data-testid="briefing-offering-fit">
                        {offerings.map((fit, idx) => (
                          <div key={idx} className="p-3 bg-[#0A1628] rounded-lg border border-[#1A2F4C]">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-white uppercase">{fit.offering || 'Offering'}</span>
                              <FitBadge rating={fit.rating} />
                            </div>
                            <p className="text-[10px] text-[#A3B5C7] leading-snug">{fit.rationale || 'N/A'}</p>
                          </div>
                        ))}
                      </div>
                    </Section>
                  );
                })()}

                {/* Relationship History */}
                {briefing.relationship_history?.length > 0 && (
                  <Section title="Relationship History" icon={Shield}>
                    {briefing.relationship_history.map((rh, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs mb-1">
                        <span className="px-2 py-0.5 bg-[#00D4C8]/10 text-[#00D4C8] rounded">{rh.stage}</span>
                        <span className="text-[#A3B5C7]">Temp: {rh.temperature}</span>
                        <span className="text-[#A3B5C7]">Last: {rh.last_contact}</span>
                        {rh.deal_value && <span className="text-[#00D4C8]">{'\u00A3'}{rh.deal_value?.toLocaleString()}</span>}
                      </div>
                    ))}
                  </Section>
                )}

                {/* Previous Outreach */}
                {briefing.previous_outreach?.length > 0 && (
                  <Section title="Previous Outreach" icon={MessageSquare}>
                    {briefing.previous_outreach.map((o, i) => (
                      <div key={i} className="p-2 bg-[#0A1628] rounded mb-2 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#00D4C8]">{o.type}</span>
                          <span className="text-[#A3B5C7]">{o.status} ({o.date})</span>
                        </div>
                        <p className="text-[#D4E2F0]">{o.summary}</p>
                      </div>
                    ))}
                  </Section>
                )}

                {/* Talking Points */}
                <Section title="Suggested Talking Points" icon={MessageSquare}>
                  {briefing.talking_points?.length > 0 ? (
                    <ol className="space-y-2">
                      {briefing.talking_points.map((tp, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-[#00D4C8] font-mono text-xs mt-0.5">{i + 1}.</span>
                          <span>{tp}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-[#A3B5C7] italic text-xs">AI talking points unavailable</p>
                  )}
                </Section>

                {/* Suggested Ask */}
                <Section title="Suggested Ask" icon={ArrowRight}>
                  <p className="font-medium">{briefing.suggested_ask || 'N/A'}</p>
                </Section>

                {/* Red Flags */}
                {briefing.red_flags?.length > 0 && (
                  <Section title="Red Flags / Watch-Outs" icon={AlertTriangle}>
                    {briefing.red_flags.map((rf, i) => (
                      <p key={i} className="text-red-400 text-xs mb-1">{rf}</p>
                    ))}
                  </Section>
                )}

                {/* Next Action */}
                <Section title="Next Action Recommendation" icon={ArrowRight}>
                  <p className="font-medium text-[#00D4C8]">{briefing.next_action || 'N/A'}</p>
                </Section>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BriefingModal;
